import type { SpriteName } from './sprites.js';

/**
 * Particle presets — the tuning knobs for every effect.
 *
 * Colors are literal RGB constants, NOT CSS-var references: the canvas renderer
 * can't read `var(--accent-blood)` at draw time, so the palette is duplicated
 * here and kept visually aligned with `theme/tokens.css` by eye. Emit positions
 * and turbulence work in a normalized 0..1 space (see engine).
 */

export type BlendMode = 'lighter' | 'source-over';

/** A [min, max] inclusive range the engine samples uniformly per particle. */
export type Range = [number, number];

export interface ParticlePreset {
  /** Which pre-rendered texture to draw. */
  sprite: SpriteName;
  /** Ambient target count at intensity 1 (bursts use `burst()`'s count arg). */
  count: number;
  /** Particle diameter in CSS px, sampled per particle. */
  size: Range;
  /** Initial velocity, CSS px/sec, sampled per axis. +y is downward. */
  velocity: { x: Range; y: Range };
  /** Constant downward accel, CSS px/sec². Negative = buoyant (rises). */
  gravity?: number;
  /** Per-second velocity retention (1 = frictionless, 0.9 = heavy drag). */
  drag?: number;
  /** Lifespan seconds, sampled per particle. */
  life: Range;
  /** Opacity [atSpawn, atDeath]; ramps linearly across life. */
  opacity: [number, number];
  /** Canvas compositing — 'lighter' for additive glow, else 'source-over'. */
  blend: BlendMode;
  /** Multiplies the (white) sprite. `[r,g,b]` 0..255. Omit for white. */
  tint?: [number, number, number];
  /** Sinusoidal horizontal wander: px amplitude + Hz-ish frequency. */
  turbulence?: { amplitude: number; frequency: number };
  /** Rotate the sprite (rad/sec range). Confetti/leaf/feather use this. */
  spin?: Range;
  /** Opacity oscillation (blink), Hz. Fireflies twinkle via this. */
  blink?: number;
}

// ── Ambient emitters ──────────────────────────────────────────────────────────

/** Warm embers drifting upward from below-frame — the signature Blood Moon bed. */
export const embers: ParticlePreset = {
  sprite: 'dot',
  count: 60,
  size: [2, 6],
  velocity: { x: [-8, 8], y: [-38, -18] },
  gravity: -6,
  drag: 0.98,
  life: [2.4, 4.2],
  opacity: [0, 0.85],
  blend: 'lighter',
  tint: [255, 90, 79], // #ff5a4f, warmed toward #c9923f near end via opacity fade
  turbulence: { amplitude: 14, frequency: 0.35 },
};

/**
 * Tall-rise ember bed for big desktop surfaces (Discord Activity): spawns bright
 * at the bottom edge and climbs ~half the screen while fading out, so the bed
 * reads as a warm glow that dissolves toward the center. Same silhouette as
 * `embers`, inverted opacity ramp (bright at spawn → transparent at death).
 */
export const embersTall: ParticlePreset = {
  sprite: 'dot',
  count: 70,
  size: [2, 6],
  velocity: { x: [-12, 12], y: [-110, -55] },
  gravity: -10,
  drag: 0.995,
  life: [5, 8],
  opacity: [0.85, 0],
  blend: 'lighter',
  tint: [255, 90, 79], // #ff5a4f
  turbulence: { amplitude: 18, frequency: 0.3 },
};

/** Tall-rise gold shimmer — the goldDust counterpart of `embersTall`. */
export const goldDustTall: ParticlePreset = {
  sprite: 'dot',
  count: 70,
  size: [1.5, 4.5],
  velocity: { x: [-12, 12], y: [-110, -55] },
  gravity: -10,
  drag: 0.995,
  life: [5, 8],
  opacity: [0.85, 0],
  blend: 'lighter',
  tint: [230, 184, 102], // #e6b866
  turbulence: { amplitude: 20, frequency: 0.3 },
};

/** Slow grey-teal smoke — low, broad, horizontal drift. Sets mood, never busy. */
export const fog: ParticlePreset = {
  sprite: 'fog',
  count: 14,
  size: [140, 280],
  velocity: { x: [-10, 10], y: [-3, 2] },
  drag: 0.995,
  life: [8, 14],
  opacity: [0, 0.14],
  blend: 'source-over',
  tint: [30, 79, 71], // #1e4f47 teal-grey
  turbulence: { amplitude: 20, frequency: 0.08 },
};

/** Tiny crimson motes falling — a haunted-air undertone for the wolf beats. */
export const bloodMotes: ParticlePreset = {
  sprite: 'dot',
  count: 50,
  size: [1.5, 3.5],
  velocity: { x: [-6, 6], y: [10, 30] },
  gravity: 8,
  drag: 0.99,
  life: [3, 6],
  opacity: [0, 0.7],
  blend: 'lighter',
  tint: [153, 0, 0], // #990000
  turbulence: { amplitude: 18, frequency: 0.22 },
};

/** Upward gold shimmer — victory / celebratory ambient. */
export const goldDust: ParticlePreset = {
  sprite: 'dot',
  count: 70,
  size: [1.5, 4.5],
  velocity: { x: [-10, 10], y: [-30, -12] },
  gravity: -4,
  drag: 0.985,
  life: [2.6, 4.8],
  opacity: [0, 0.9],
  blend: 'lighter',
  tint: [230, 184, 102], // #e6b866
  turbulence: { amplitude: 16, frequency: 0.4 },
};

/** Sparse blinking fireflies — quiet, magical negative space. */
export const fireflies: ParticlePreset = {
  sprite: 'dot',
  count: 16,
  size: [2, 5],
  velocity: { x: [-14, 14], y: [-10, 10] },
  drag: 0.96,
  life: [4, 8],
  opacity: [0, 0.95],
  blend: 'lighter',
  tint: [247, 227, 176], // #f7e3b0
  turbulence: { amplitude: 26, frequency: 0.5 },
  blink: 0.7,
};

/** Warm-grey ash flakes sinking slowly with a sway — aftermath / burnt village. */
export const ash: ParticlePreset = {
  sprite: 'flake',
  count: 40,
  size: [3, 8],
  velocity: { x: [-8, 8], y: [8, 22] },
  gravity: 5,
  drag: 0.99,
  life: [4, 7],
  opacity: [0, 0.55],
  blend: 'source-over',
  tint: [156, 143, 134], // #9c8f86
  turbulence: { amplitude: 30, frequency: 0.18 },
  spin: [-1.2, 1.2],
};

/** Violet arcana wisps orbiting on a lazy drift — seer / magic ambient. */
export const arcanaWisps: ParticlePreset = {
  sprite: 'wisp',
  count: 24,
  size: [10, 26],
  velocity: { x: [-16, 16], y: [-16, 16] },
  drag: 0.97,
  life: [3.5, 6.5],
  opacity: [0, 0.6],
  blend: 'lighter',
  tint: [139, 92, 246], // #8b5cf6
  turbulence: { amplitude: 34, frequency: 0.45 },
  spin: [-0.6, 0.6],
};

// ── Burst emitters (one-shot; some double as ambient) ─────────────────────────

/** Small white-gold pop — the all-purpose "tap landed" confirmation spark. */
export const sparkBurst: ParticlePreset = {
  sprite: 'dot',
  count: 18,
  size: [2, 5],
  velocity: { x: [-140, 140], y: [-140, 140] },
  gravity: 120,
  drag: 0.9,
  life: [0.4, 0.9],
  opacity: [1, 0],
  blend: 'lighter',
  tint: [255, 240, 200],
};

/** Radial crimson splatter with gravity — a kill / lynch / elimination hit. */
export const bloodBurst: ParticlePreset = {
  sprite: 'dot',
  count: 40,
  size: [3, 9],
  velocity: { x: [-220, 220], y: [-260, 120] },
  gravity: 520,
  drag: 0.86,
  life: [0.5, 1.1],
  opacity: [1, 0],
  blend: 'lighter',
  tint: [153, 0, 0], // #990000
};

/** Celebratory upward fountain of gold — victory moment. */
export const goldBurst: ParticlePreset = {
  sprite: 'wisp',
  count: 46,
  size: [6, 16],
  velocity: { x: [-120, 120], y: [-420, -180] },
  gravity: 360,
  drag: 0.92,
  life: [0.9, 1.8],
  opacity: [1, 0],
  blend: 'lighter',
  tint: [230, 184, 102], // #e6b866
  spin: [-3, 3],
};

/** Falling laurel leaves — victory confetti. Burst OR gentle ambient. */
export const laurelLeaves: ParticlePreset = {
  sprite: 'leaf',
  count: 30,
  size: [16, 34],
  velocity: { x: [-60, 60], y: [40, 140] },
  gravity: 90,
  drag: 0.97,
  life: [1.8, 3.4],
  opacity: [0.95, 0.9],
  blend: 'source-over',
  tint: [140, 176, 92], // laurel green
  turbulence: { amplitude: 40, frequency: 0.6 },
  spin: [-4, 4],
};

/** Falling raven feathers — dark/villain confetti. Burst OR ambient. */
export const ravenFeathers: ParticlePreset = {
  sprite: 'feather',
  count: 26,
  size: [18, 40],
  velocity: { x: [-70, 70], y: [30, 120] },
  gravity: 70,
  drag: 0.975,
  life: [2.2, 4],
  opacity: [0.9, 0.85],
  blend: 'source-over',
  tint: [40, 34, 46], // near-black violet
  turbulence: { amplitude: 46, frequency: 0.5 },
  spin: [-3, 3],
};

// ── Registry ──────────────────────────────────────────────────────────────────

export const PRESETS = {
  embers,
  embersTall,
  goldDustTall,
  fog,
  bloodMotes,
  goldDust,
  fireflies,
  ash,
  arcanaWisps,
  sparkBurst,
  bloodBurst,
  goldBurst,
  laurelLeaves,
  ravenFeathers,
} as const;

export type PresetName = keyof typeof PRESETS;

/** Presets that read well as a continuous ambient bed. */
export const AMBIENT_PRESETS: PresetName[] = [
  'embers',
  'embersTall',
  'goldDustTall',
  'fog',
  'bloodMotes',
  'goldDust',
  'fireflies',
  'ash',
  'arcanaWisps',
  'laurelLeaves',
  'ravenFeathers',
];

/** Presets meant to be fired as one-shot bursts. */
export const BURST_PRESETS: PresetName[] = [
  'sparkBurst',
  'bloodBurst',
  'goldBurst',
  'laurelLeaves',
  'ravenFeathers',
];
